import { ACCOUNT_ROLE_CODES } from '@lib/types';
import { Account } from '@lib/types/account';
import { Route } from '@lib/types/route';
import { findMatchingRoute } from '@lib/utils/route';

import { getRoutes as AccountsRoutes } from './accounts/routes';
import { getRoutes as AddressesRoutes } from './addresses/routes';
import { getRoutes as AdministrationRoutes } from './administration/routes';
import { getRoutes as CartRoutes } from './cart/routes';
import { getRoutes as CatalogRoutes } from './catalog/routes';
import { getRoutes as CouponsRoutes } from './coupons/routes';
import {
  getRoutes as DecksRoutes,
  getPublicRoutes as PublicDeckRoutes,
} from './decks/routes';
import {
  getRoutes as EventsRoutes,
  getPublicRoutes as PublicEventRoutes,
} from './events/routes';
import { getRoutes as FinanceRoutes } from './finance/routes';
import { getRoutes as HelpRoutes } from './help/routes';
import { getRoutes as InventoryRoutes } from './inventory/routes';
import { getRoutes as LandingRoutes } from './landing/routes';
import {
  getPublicRoutes as LocalPublicRoutes,
  getRoutes as LocalRoutes,
} from './local/routes';
import { getRoutes as OrdersRoutes } from './orders/routes';
import { getRoutes as PaymentSettingsRoutes } from './payment-settings/routes';
import { getRoutes as ProductsRoutes } from './products/routes';
import { ROUTE_PATHS } from './route-paths';
import { getRoutes as ShippingSettingsRoutes } from './shipping-settings/routes';
import { getRoutes as StoresRoutes } from './stores/routes';
import { getRoutes as UnauthRoutes } from './unauth/routes';
import { getRoutes as UserRoutes } from './user/routes';

export const getAdminRoutes = (): Route[] => [
  ...LandingRoutes(),
  ...AdministrationRoutes(),
  ...EventsRoutes(),
  ...AccountsRoutes(),
  ...AddressesRoutes(),
  ...CatalogRoutes(),
  ...DecksRoutes(),
  ...PublicDeckRoutes(),
  ...PublicEventRoutes(),
  ...CartRoutes(),
  ...CouponsRoutes(),
  ...InventoryRoutes(),
  ...FinanceRoutes(),
  ...HelpRoutes(),
  ...OrdersRoutes(),
  ...PaymentSettingsRoutes(),
  ...ProductsRoutes(),
  ...StoresRoutes(),
  ...LocalRoutes(),
  ...LocalPublicRoutes(),
  ...ShippingSettingsRoutes(),
  ...UserRoutes(),
];

export const getClientRoutes = (): Route[] => [
  ...LandingRoutes(),
  ...AddressesRoutes(),
  ...CatalogRoutes(),
  ...DecksRoutes(),
  ...PublicDeckRoutes(),
  ...PublicEventRoutes(),
  ...CartRoutes(),
  ...CouponsRoutes(),
  ...InventoryRoutes(),
  ...FinanceRoutes(),
  ...HelpRoutes(),
  ...OrdersRoutes(),
  ...PaymentSettingsRoutes(),
  ...ProductsRoutes(),
  ...StoresRoutes(),
  ...LocalRoutes(),
  ...LocalPublicRoutes(),
  ...UserRoutes(),
];

export const getModuleRoutesByAccount = (account?: Account | null): Route[] => {
  const onlyRoutesAllowedWithoutVerification = (routes: Route[]): Route[] =>
    routes.reduce<Route[]>((allowedRoutes, route) => {
      if (route.children) {
        const children = route.children.filter(
          (child) => child.allowUnverified,
        );

        return children.length
          ? [...allowedRoutes, { ...route, children }]
          : allowedRoutes;
      }

      return route.allowUnverified ? [...allowedRoutes, route] : allowedRoutes;
    }, []);

  if (account && !account.verifiedAt) {
    return onlyRoutesAllowedWithoutVerification(getClientRoutes());
  }

  if (account?.role === ACCOUNT_ROLE_CODES.ADMIN) {
    return getAdminRoutes();
  }

  if (account) {
    return getClientRoutes();
  }

  return [
    ...UnauthRoutes(),
    ...PublicDeckRoutes(),
    ...PublicEventRoutes(),
    ...LocalPublicRoutes(),
  ];
};

export const getDefaultRoutePath = (account?: Account | null): string => {
  if (account && !account.verifiedAt) {
    return ROUTE_PATHS.PROFILE;
  }

  if (account) {
    return ROUTE_PATHS.HOME;
  }

  return ROUTE_PATHS.LOGIN;
};

export const getAllModulesRoutes = (): Route[] => [
  ...UnauthRoutes(),
  ...LandingRoutes(),
  ...AdministrationRoutes(),
  ...EventsRoutes(),
  ...AddressesRoutes(),
  ...AccountsRoutes(),
  ...CatalogRoutes(),
  ...DecksRoutes(),
  ...PublicDeckRoutes(),
  ...PublicEventRoutes(),
  ...CartRoutes(),
  ...CouponsRoutes(),
  ...InventoryRoutes(),
  ...FinanceRoutes(),
  ...HelpRoutes(),
  ...OrdersRoutes(),
  ...PaymentSettingsRoutes(),
  ...ProductsRoutes(),
  ...StoresRoutes(),
  ...LocalRoutes(),
  ...LocalPublicRoutes(),
  ...ShippingSettingsRoutes(),
  ...UserRoutes(),
];

export const isPublicRoutePath = (pathname: string): boolean =>
  findMatchingRoute(getAllModulesRoutes(), pathname)?.public === true;
